import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { useDispatch, useSelector } from 'react-redux';
import { 
    Typography, 
    FormControl, 
    Select, 
    MenuItem, 
    InputLabel, 
    Paper, 
    Box
} from '@material-ui/core';
import * as colors from '@material-ui/core/colors';
import moment from 'moment';
import * as Actions from '../../store/actions';

const MetricHistoryChart = ({ clusterId, metricType, title, unit, className, preSelectedNode = null, hideTitle = false }) => {
    const dispatch = useDispatch();
    const [chartData, setChartData] = useState(null);
    const [timeRange, setTimeRange] = useState('6h');
    const [selectedNode, setSelectedNode] = useState(preSelectedNode || 'all');
    
    // Update selectedNode when preSelectedNode prop changes
    useEffect(() => {
        if (preSelectedNode && preSelectedNode !== selectedNode) {
            setSelectedNode(preSelectedNode);
        }
    }, [preSelectedNode]);
    
    // Get cluster metrics history from Redux store
    const { 
        history: clusterMetricsHistory,
        loading,
        error 
    } = useSelector(({ clusters }) => clusters.cluster.clusterMetrics);
    
    // Get socket-based change flags for real-time updates
    const changedDbClusterHealth = useSelector(({ providers }) => providers.providers.changedDbClusterHealth);
    const changedDbClusters = useSelector(({ providers }) => providers.providers.changedDbClusters);
    const changedDbNodes = useSelector(({ providers }) => providers.providers.changedDbNodes);

    const timeRangeOptions = [
        { value: '1h', label: '1 Hour' },
        { value: '6h', label: '6 Hours' },
        { value: '24h', label: '24 Hours' },
        { value: '7d', label: '7 Days' },
        { value: '30d', label: '30 Days' }
    ];

    // Get available nodes for selection from cluster metrics
    const getAvailableNodes = () => {
        if (!clusterMetricsHistory || clusterMetricsHistory.length === 0) return [];
        
        const nodeSet = new Set();
        clusterMetricsHistory.forEach(metric => {
            if (metric.nodes) {
                metric.nodes.forEach(node => {
                    nodeSet.add(node.hostname);
                });
            }
        });
        
        return Array.from(nodeSet).sort();
    };

    // Process metrics data for the specific metric type
    const processMetricsData = (metrics) => {
        if (!metrics || metrics.length === 0) {
            setChartData(null);
            return;
        }

        // Sort metrics by timestamp
        const sortedMetrics = [...metrics].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        
        // Process data based on metric type and node selection
        const timeLabels = [];
        const datasets = [];

        if (selectedNode === 'all') {
            // Show aggregated data from summary
            const aggregatedData = [];
            
            sortedMetrics.forEach(metric => {
                let timeLabel;
                // Dynamic time format based on time range
                if (timeRange === '1h' || timeRange === '6h') {
                    timeLabel = moment(metric.timestamp).format('HH:mm');
                } else if (timeRange === '24h') {
                    timeLabel = moment(metric.timestamp).format('MM/DD HH:mm');
                } else if (timeRange === '7d') {
                    timeLabel = moment(metric.timestamp).format('MM/DD');
                } else { // 30d
                    timeLabel = moment(metric.timestamp).format('MM/DD');
                }
                timeLabels.push(timeLabel);
                
                let value = 0;
                switch (metricType) {
                    case 'cpu':
                        value = metric.summary?.avgCpuLoad || 0;
                        break;
                    case 'memory':
                        value = metric.summary?.avgMemoryUsage || 0;
                        break;
                    case 'disk':
                        value = metric.summary?.avgDiskUsage || 0;
                        break;
                    case 'iops':
                        value = metric.summary?.totalIOPS || 0;
                        break;
                    case 'storage':
                        value = metric.summary?.totalStorageGB || 0;
                        break;
                    case 'totalMemory':
                        value = metric.summary?.totalMemoryGB || 0;
                        break;
                    case 'nodes':
                        value = metric.summary?.totalNodes || 0;
                        break;
                    case 'network':
                        value = metric.summary?.avgNetworkIO || 0;
                        break;
                    case 'gpu':
                        value = metric.summary?.avgGpuUtilization || 0;
                        break;
                    case 'totalGpus':
                        value = metric.summary?.activeGpus || metric.summary?.totalGpus || 0;
                        break;
                    default:
                        value = 0;
                }
                aggregatedData.push(value);
            });

            datasets.push({
                label: `Cluster ${title}`,
                data: aggregatedData,
                borderColor: colors.blue[500],
                backgroundColor: 'transparent',
                fill: false,
                pointRadius: 2,
                pointHoverRadius: 4,
                borderWidth: 2
            });
        } else {
            // Show individual node data
            const nodeData = [];
            
            sortedMetrics.forEach(metric => {
                let timeLabel;
                // Dynamic time format based on time range
                if (timeRange === '1h' || timeRange === '6h') {
                    timeLabel = moment(metric.timestamp).format('HH:mm');
                } else if (timeRange === '24h') {
                    timeLabel = moment(metric.timestamp).format('MM/DD HH:mm');
                } else if (timeRange === '7d') {
                    timeLabel = moment(metric.timestamp).format('MM/DD');
                } else { // 30d
                    timeLabel = moment(metric.timestamp).format('MM/DD');
                }
                
                if (!timeLabels.includes(timeLabel)) {
                    timeLabels.push(timeLabel);
                }
                
                // Find the specific node data
                const nodeMetric = metric.nodes?.find(node => node.hostname === selectedNode);
                let value = 0;
                
                if (nodeMetric) {
                    switch (metricType) {
                        case 'cpu':
                            value = nodeMetric.system?.loadAverage?.['1min'] || 0;
                            break;
                        case 'memory':
                            value = nodeMetric.system?.memory?.usagePercent || 0;
                            break;
                        case 'disk':
                            value = nodeMetric.system?.disk?.usagePercent || 0;
                            break;
                        case 'iops':
                            value = nodeMetric.system?.disk?.totalIOPS || 0;
                            break;
                        case 'storage':
                            value = nodeMetric.system?.disk?.total ? 
                                (nodeMetric.system.disk.total / (1024 * 1024 * 1024)) : 0;
                            break;
                        case 'totalMemory':
                            value = nodeMetric.system?.memory?.total ? 
                                (nodeMetric.system.memory.total / (1024 * 1024 * 1024)) : 0;
                            break;
                        case 'network':
                            value = nodeMetric.network ? 
                                ((nodeMetric.network.totalTransmitted || 0) + (nodeMetric.network.totalReceived || 0)) / (1024 * 1024) : 0;
                            break;
                        case 'gpu':
                            value = nodeMetric.gpu?.utilization || 0;
                            break;
                        case 'nodes':
                            value = 1; // Single node
                            break;
                        case 'totalGpus':
                            value = nodeMetric.gpu?.count || (nodeMetric.gpu ? 1 : 0);
                            break;
                        default:
                            value = 0;
                    }
                }
                nodeData.push(value);
            });

            datasets.push({
                label: `${selectedNode} ${title}`,
                data: nodeData,
                borderColor: colors.green[500],
                backgroundColor: 'transparent',
                fill: false,
                pointRadius: 2,
                pointHoverRadius: 4,
                borderWidth: 2
            });
        }

        setChartData({
            labels: timeLabels,
            datasets
        });
    };

    // Fetch data when component mounts or parameters change
    useEffect(() => {
        if (clusterId) {
            const hours = timeRange === '1h' ? 1 : 
                        timeRange === '6h' ? 6 : 
                        timeRange === '24h' ? 24 : 
                        timeRange === '7d' ? 168 : 720; // 30d = 720h
            
            // Always fetch full history data (not filtered by node) so we have all nodes available
            dispatch(Actions.fetchClusterMetricsHistory(clusterId, hours, 'all'));
        }
    }, [dispatch, clusterId, timeRange]); // Remove selectedNode from dependencies

    // Process data when history changes
    useEffect(() => {
        if (clusterMetricsHistory) {
            processMetricsData(clusterMetricsHistory);
        }
    }, [clusterMetricsHistory, metricType, selectedNode]);

    // Re-fetch on real-time updates
    useEffect(() => {
        if ((changedDbClusterHealth || changedDbClusters || changedDbNodes) && clusterId) {
            const hours = timeRange === '1h' ? 1 : 
                        timeRange === '6h' ? 6 : 
                        timeRange === '24h' ? 24 : 
                        timeRange === '7d' ? 168 : 720;
            
            dispatch(Actions.fetchClusterMetricsHistory(clusterId, hours, selectedNode));
        }
    }, [changedDbClusterHealth, changedDbClusters, changedDbNodes, clusterId, timeRange, selectedNode, dispatch]);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'top'
            },
            title: {
                display: true,
                text: `${title} History (${timeRange})`
            }
        },
        scales: {
            x: {
                display: true,
                title: {
                    display: true,
                    text: 'Time'
                },
                ticks: {
                    maxTicksLimit: timeRange === '30d' ? 10 : timeRange === '7d' ? 14 : 20,
                    autoSkip: true
                }
            },
            y: {
                display: true,
                title: {
                    display: true,
                    text: `${title} (${unit})`
                },
                beginAtZero: metricType !== 'storage' && metricType !== 'totalMemory'
            }
        },
        interaction: {
            intersect: false,
            mode: 'index'
        },
        animation: {
            duration: 0 // Disable all animations
        },
        transitions: {
            active: {
                animation: {
                    duration: 0
                }
            }
        }
    };

    return (
        <Paper className={className} style={{ padding: 16 }}>
            <Box display="flex" justifyContent={hideTitle ? "flex-end" : "space-between"} alignItems="center" marginBottom={2} paddingBottom={1}>
                {!hideTitle && (
                    <Typography variant="h6">{title} History</Typography>
                )}
                
                <Box display="flex" gap={2}>
                    <FormControl size="small" style={{ minWidth: 130 }} variant="outlined">
                        <InputLabel>Time Range</InputLabel>
                        <Select
                            value={timeRange}
                            onChange={(e) => setTimeRange(e.target.value)}
                            label="Time Range"
                        >
                            {timeRangeOptions.map(option => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" style={{ minWidth: 120 }} variant="outlined">
                        <InputLabel>Node</InputLabel>
                        <Select
                            value={selectedNode}
                            onChange={(e) => setSelectedNode(e.target.value)}
                            label="Node"
                        >
                            <MenuItem value="all">All Nodes</MenuItem>
                            {getAvailableNodes().map(node => (
                                <MenuItem key={node} value={node}>
                                    {node}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            <Box height={280}>
                {error ? (
                    <Box 
                        display="flex" 
                        flexDirection="column"
                        justifyContent="center" 
                        alignItems="center" 
                        height="100%"
                    >
                        <Typography variant="body1" color="error" style={{ marginBottom: 8 }}>
                            Error loading data
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {error}
                        </Typography>
                    </Box>
                ) : chartData ? (
                    <Line data={chartData} options={chartOptions} />
                ) : (
                    <Box 
                        display="flex" 
                        flexDirection="column"
                        justifyContent="center" 
                        alignItems="center" 
                        height="100%"
                    >
                        <Typography variant="body1" color="textSecondary" style={{ marginBottom: 8 }}>
                            No Data Available
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Select a different time range or check data availability
                        </Typography>
                    </Box>
                )}
            </Box>
        </Paper>
    );
};

export default MetricHistoryChart;
